import { throwError } from "../../lib/throwError";
import { get, post } from "../axiosUitils";
import {
  Event,
  EventListResponse,
  EventDetailsResponse,
} from "../../utils/types/event";

const basePath = "/city_collab/events";

class EventService {
  /**
   * Get all events
   */
  getAllEvents = async (
    notifyMessage?: boolean,
    filters?: { organization_id?: string; event?: string }
  ): Promise<Event[]> => {
    try {
      const params: Record<string, string> = {};
      if (filters?.organization_id) {
        params.organization_id = filters.organization_id;
      }
      if (filters?.event) {
        params.event = filters.event;
      }

      const response = await get<EventListResponse>(`${basePath}/`, {
        params,
        notifyMessage,
      });

      if (response?.success && response?.data) {
        return response.data;
      }

      return [];
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch events", "EventService");
      return [];
    }
  };

  /**
   * Get event by ID
   */
  getEventById = async (
    id: string,
    notifyMessage?: boolean
  ): Promise<Event | null> => {
    if (!id) {
      throwError("Event ID is required", "EventService");
      return null;
    }

    try {
      const response = await get<EventListResponse>(`${basePath}/`, {
        notifyMessage,
      });

      if (response?.success && response?.data) {
        const event = response.data.find(e => e.id === id);
        return event ?? null;
      }

      return null;
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch event", "EventService");
      return null;
    }
  };

  /**
   * Get event details by ID from details endpoint
   */
  getEventDetails = async (
    eventId: string,
    notifyMessage?: boolean
  ): Promise<Event | null> => {
    if (!eventId) {
      throwError("Event ID is required", "EventService");
      return null;
    }

    try {
      const response = await get<EventDetailsResponse>(`${basePath}/details/`, {
        params: {
          event_id: eventId,
        },
        notifyMessage,
      });

      if (response?.success && response?.data) {
        return response.data;
      }

      return null;
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch event details", "EventService");
      return null;
    }
  };

  /**
   * Express interest in an event
   */
  expressEventInterest = async (
    eventId: string,
    interested: boolean,
    notifyMessage?: boolean
  ): Promise<any> => {
    if (!eventId) {
      throwError("Event ID is required", "EventService");
      return null;
    }

    try {
      const response = await post(
        "/city_collab/event-interest/",
        {
          event_id: eventId,
          interested: interested,
        },
        {
          notifyMessage,
        }
      );

      return response;
    } catch (error) {
      console.log(error);
      throwError("Failed to express interest in event", "EventService");
      throw error;
    }
  };

  /**
   * Buy tickets for an event
   */
  buyTickets = async (
    payload: {
      eventid: string;
      total_amount: string;
      fullname: string;
      email: string;
      mobile: string;
      tickets: Array<{
        ticket_id: string;
        no_of_ticket: number;
        price: string;
        amount: string;
      }>;
    },
    notifyMessage?: boolean
  ): Promise<any> => {
    if (!payload.eventid) {
      throwError("Event ID is required", "EventService");
      return null;
    }

    if (!payload.tickets || payload.tickets.length === 0) {
      throwError("At least one ticket is required", "EventService");
      return null;
    }

    try {
      const response = await post("/city_collab/buy-tickets/", payload, {
        notifyMessage,
      });

      return response;
    } catch (error) {
      console.log(error);
      throwError("Failed to buy tickets", "EventService");
      throw error;
    }
  };

  /**
   * Get event tickets by event ID
   */
  getEventTickets = async (
    eventId: string,
    notifyMessage?: boolean
  ): Promise<any[]> => {
    if (!eventId) {
      throwError("Event ID is required", "EventService");
      return [];
    }

    try {
      const response = await get<any>("/city_collab/event-tickets/", {
        params: {
          event_id: eventId,
        },
        notifyMessage,
      });

      // Handle different response structures
      if (response?.data && Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response)) {
        return response;
      }

      return [];
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch event tickets", "EventService");
      return [];
    }
  };

  /**
   * Submit a donation for an event
   */
  submitDonation = async (
    payload: {
      event_id: string;
      name: string;
      email: string;
      phone: string;
      donation_amount: string;
      message?: string;
      donation_id?: string;
    },
    notifyMessage?: boolean
  ): Promise<any> => {
    if (!payload.event_id) {
      throwError("Event ID is required", "EventService");
      return null;
    }

    if (
      !payload.name ||
      !payload.email ||
      !payload.phone ||
      !payload.donation_amount
    ) {
      throwError(
        "Name, email, phone, and donation amount are required",
        "EventService"
      );
      return null;
    }

    try {
      const response = await post("/city_collab/donations/", payload, {
        notifyMessage,
      });

      return response;
    } catch (error) {
      console.log(error);
      throwError("Failed to submit donation", "EventService");
      throw error;
    }
  };

  /**
   * Get donations for an event
   */
  getEventDonations = async (
    eventId: string,
    notifyMessage?: boolean
  ): Promise<any[]> => {
    if (!eventId) {
      throwError("Event ID is required", "EventService");
      return [];
    }

    try {
      const response = await get<any>("/city_collab/donations/list/", {
        params: {
          event_id: eventId,
        },
        notifyMessage,
      });

      // Handle paginated response structure with results array
      // The get function returns response.data, so response already contains the API response
      if (response?.results && Array.isArray(response.results)) {
        return response.results;
      } else if (
        response?.data?.results &&
        Array.isArray(response.data.results)
      ) {
        return response.data.results;
      } else if (response?.data && Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response)) {
        return response;
      }

      return [];
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch donations", "EventService");
      return [];
    }
  };

  /**
   * Submit a sponsorship interest / pledge for an event
   */
  submitSponsor = async (
    payload: {
      event_id: string;
      sponsor_id: string;
      name: string;
      email: string;
      phone: string;
      sponsor_amount: number;
      message?: string;
    },
    notifyMessage?: boolean
  ): Promise<any> => {
    if (!payload.event_id || !payload.sponsor_id) {
      throwError("Event ID and sponsor ID are required", "EventService");
      return null;
    }

    if (
      !payload.name ||
      !payload.email ||
      !payload.phone ||
      payload.sponsor_amount === undefined ||
      payload.sponsor_amount === null ||
      Number.isNaN(Number(payload.sponsor_amount))
    ) {
      throwError(
        "Name, email, phone, and sponsor amount are required",
        "EventService"
      );
      return null;
    }

    try {
      const body = {
        ...payload,
        sponsor_amount: Number(payload.sponsor_amount),
      };
      const response = await post("/city_collab/sponsors/", body, {
        notifyMessage,
      });
      return response;
    } catch (error) {
      console.log(error);
      throwError("Failed to submit sponsorship", "EventService");
      throw error;
    }
  };

  /**
   * Sponsorship records for the logged-in user
   */
  getLoginUserSponsorList = async (notifyMessage?: boolean): Promise<any> => {
    try {
      const response = await get<any>(
        "/city_collab/sponsors/login-user-sponsor-list/",
        { notifyMessage }
      );
      return response;
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch sponsor list", "EventService");
      return null;
    }
  };

  /**
   * Signup as a volunteer for an activity
   */
  volunteerSignup = async (
    volunteerActivityId: string,
    notifyMessage?: boolean
  ): Promise<any> => {
    if (!volunteerActivityId) {
      throwError("Volunteer Activity ID is required", "EventService");
      return null;
    }

    try {
      const response = await post(
        "/city_collab/volunteer-signup/",
        {
          volunteer_activity_id: volunteerActivityId,
        },
        {
          notifyMessage,
        }
      );

      return response;
    } catch (error) {
      console.log(error);
      throwError("Failed to signup for volunteer activity", "EventService");
      throw error;
    }
  };

  /**
   * Get volunteer signups for the current user
   */
  getVolunteerSignups = async (notifyMessage?: boolean): Promise<any> => {
    try {
      const response = await get<any>(
        "/city_collab/volunteer-signup/volunteer-events/",
        {
          notifyMessage,
        }
      );
      return response;
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch volunteer signups", "EventService");
      return null;
    }
  };

  /**
   * Get volunteer signup list for current user
   */
  getVolunteerSignupList = async (notifyMessage?: boolean): Promise<any> => {
    try {
      const response = await get<any>("/city_collab/volunteer-signup/", {
        notifyMessage,
      });
      return response;
    } catch (error) {
      console.log(error);
      throwError("Failed to fetch volunteer signup list", "EventService");
      return null;
    }
  };
}

const instance = new EventService();
export default instance;
